import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import Toggler from 'components/form/Toggler.component';
import Badge from 'components/generic/Badge.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Tooltip from 'components/generic/Tooltip.component';
import TooltipContentReceptionist from 'components/generic/TooltipContentReceptionist.component';
import CurrencySign from 'components/price/CurrencySign.component';
import DEPOSIT_TYPE from 'constants/settings/ncobDepositSettings.const';
import S from 'constants/string.const';
import { useOnlineBookingPermissions } from 'hooks/useOnlineBookingPermissions.hook';
import useTip from 'hooks/useTip.hook';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonStripeAccount } from 'selectors/currentSalon.selectors';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import logger from 'utils/logger.util';

import { fetchSalonState } from 'salon/actions';
import { setSalonNoshowOrDeposit } from 'settings/online-booking/actions';
import BeforeSetupStripeModal from 'settings/online-booking/modals/BeforeSetupStripeModal';
import DepositModal from 'settings/page-elements/setup-nsp-deposit/modals/DepositModal';
import StripeModal from 'settings/page-elements/setup-nsp-deposit/modals/StripeModal';

import CN from './DepositSetup.module.scss';

const L = logger('DepositSetup');

const ROADMAP_LINK = 'http://help.getslick.com/en/articles/4175597-online-deposit-payments';

export const DepositSetup = ({ onDepositCheckboxClick }) => {
  const dispatch = useDispatch();
  const salonId = useCurrentSalonId();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const { isSalonInGB } = useGilt();

  const hasStripeAccount = useSelector(selectCurrentSalonStripeAccount);
  const { canManageDepositSettings } = useOnlineBookingPermissions();
  const isReadOnly = !canManageDepositSettings;
  const randomKey = useSelector((state) => state.currentSalon.random_key);
  const isFetchingRandomKey = useSelector((state) => state.currentSalon.isFetchingRandomKey);
  const ncobDepositSettings = useSelector((state) => state.currentSalon.ncob_deposit_settings);
  const ncobCustomAmount = useSelector((state) => state.currentSalon.ncob_custom_amount);

  const { val: depositModal, yay: showDepositModal, nay: hideDepositModal } = useTip(false);
  const { val: paymentFeesModal, yay: showPaymentFeesModal, tip: togglePaymentFeesModal } = useTip(false);
  const { val: stripeModal } = useTip(false);

  useEffect(() => {
    if (!randomKey && !isFetchingRandomKey) {
      L.unignore('useEffect()', 'fetchSalonState:', dispatch(fetchSalonState(fromAnyToString(salonId))));
    }
  }, [dispatch, isFetchingRandomKey, randomKey, salonId]);

  const handleSaveDeposit = useCallback(
    (data) => {
      if (hasStripeAccount) {
        hideDepositModal();
      } else {
        showPaymentFeesModal();
        hideDepositModal();
      }
      dispatch(setSalonNoshowOrDeposit(data, salonId));
    },
    [dispatch, hasStripeAccount, hideDepositModal, salonId, showPaymentFeesModal],
  );

  const handleDepositCheckboxClick = useCallback(() => {
    if (
      ncobDepositSettings === DEPOSIT_TYPE.fullAmount ||
      ncobDepositSettings === DEPOSIT_TYPE.customAmount ||
      ncobDepositSettings === DEPOSIT_TYPE.halfAmount ||
      ncobDepositSettings === DEPOSIT_TYPE.quarterAmount
    ) {
      onDepositCheckboxClick();
    }

    if (ncobDepositSettings === DEPOSIT_TYPE.nullAmount) showDepositModal();
  }, [ncobDepositSettings, onDepositCheckboxClick, showDepositModal]);

  const renderedCurrentDepositSetting = () => {
    const depositSettings = {
      [DEPOSIT_TYPE.fullAmount]: t('onlineBooking.generalSettings.businessStripeAccount.upfront', { percent: 100 }),
      [DEPOSIT_TYPE.halfAmount]: t('onlineBooking.generalSettings.businessStripeAccount.upfront', { percent: 50 }),
      [DEPOSIT_TYPE.quarterAmount]: t('onlineBooking.generalSettings.businessStripeAccount.upfront', { percent: 25 }),
      [DEPOSIT_TYPE.customAmount]: (
        <>
          <CurrencySign />
          {ncobCustomAmount} {t('onlineBooking.generalSettings.businessStripeAccount.upfrontLabel')}
        </>
      ),
      [DEPOSIT_TYPE.nullAmount]: t('onlineBooking.generalSettings.businessStripeAccount.upfront', { percent: 0 }),
    };

    return (
      <>
        {t('onlineBooking.generalSettings.businessStripeAccount.currentCharge')}{' '}
        {depositSettings[ncobDepositSettings] || t('onlineBooking.generalSettings.businessStripeAccount.upfront', { percent: 0 })}
      </>
    );
  };

  const isDepositEnabled = ncobDepositSettings === DEPOSIT_TYPE.nullAmount;

  return (
    <div data-bem="DepositSetup" data-testid="DepositSetup">
      <div
        className={cn({
          [CN.component]: true,
          [CN.darkMode]: isDarkMode,
        })}
      >
        {!hasStripeAccount ? (
          <>
            <div className={CN.content}>
              <div className={CN.title}>{t('onlineBooking.generalSettings.businessStripeAccount.header2')}</div>
              <div className={CN.text}>{t('onlineBooking.generalSettings.businessStripeAccount.description2')}</div>

              <div className={CN.buttonsContainer}>
                {isReadOnly ? (
                  <Tooltip variant="slickTop" content={<TooltipContentReceptionist />}>
                    <Button variant="primary" data-testid="DepositSetup__setup-button">
                      {t('onlineBooking.generalSettings.businessStripeAccount.confirmButton')}
                    </Button>
                  </Tooltip>
                ) : (
                  <Button tag="DepositSetup__setup-button" data-testid="DepositSetup__setup-button" onClick={togglePaymentFeesModal} variant="primary">
                    {t('onlineBooking.generalSettings.businessStripeAccount.confirmButton')}
                  </Button>
                )}

                {isSalonInGB && (
                  <ExternalLink link={ROADMAP_LINK} variant="primary">
                    {t('onlineBooking.generalSettings.needHelp')}
                  </ExternalLink>
                )}
              </div>
            </div>
            <div className={CN.imageContainer} />
          </>
        ) : (
          <div className={CN.content}>
            <div className={CN.title}>
              <div className={CN.flex}>
                {t('onlineBooking.generalSettings.businessStripeAccount.header2')}
                <div className={CN.gap}>
                  <Badge variant="fine" disabled={isDepositEnabled}>
                    <span className={CN.emojiSize}>{S.moneyBag}</span>
                    {S.space}
                    {renderedCurrentDepositSetting()}
                  </Badge>
                </div>
              </div>
              {isReadOnly ? (
                <Tooltip variant="slickTop" content={<TooltipContentReceptionist />}>
                  <Toggler tag="DepositSetup-toggle" data-testid="DepositSetup-toggle" selected={ncobDepositSettings !== DEPOSIT_TYPE.nullAmount} />
                </Tooltip>
              ) : (
                <Toggler tag="DepositSetup-toggle" data-testid="DepositSetup-toggle" onClick={handleDepositCheckboxClick} selected={ncobDepositSettings !== DEPOSIT_TYPE.nullAmount} />
              )}
            </div>
            <div className={CN.text}>{t('onlineBooking.generalSettings.businessStripeAccount.description2')}</div>
            <div className={CN.text}>{t('onlineBooking.generalSettings.businessStripeAccount.description3')}</div>
            <div className={CN.negativeGap}>
              {isReadOnly ? (
                <Tooltip variant="slickTop" content={<TooltipContentReceptionist />}>
                  <Button variant="slim" disabled={isDepositEnabled} data-testid="DepositSetup__edit-deposit">
                    {t('onlineBooking.generalSettings.businessStripeAccount.editDeposit')}
                  </Button>
                </Tooltip>
              ) : (
                <>
                  {isDepositEnabled ? (
                    <Tooltip variant="top" width="md" content={t('onlineBooking.generalSettings.businessStripeAccount.tooltip')}>
                      <Button onClick={showDepositModal} variant="slim" disabled data-testid="DepositSetup__edit-deposit">
                        {t('onlineBooking.generalSettings.businessStripeAccount.editDeposit')}
                      </Button>
                    </Tooltip>
                  ) : (
                    <Button onClick={showDepositModal} variant="slim" data-testid="DepositSetup__edit-deposit">
                      {t('onlineBooking.generalSettings.businessStripeAccount.editDeposit')}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {fromAnyToBool(depositModal) && <DepositModal onSave={handleSaveDeposit} onClose={hideDepositModal} ncob_deposit_settings={ncobDepositSettings} ncob_custom_amount={ncobCustomAmount} />}

      <BeforeSetupStripeModal isOpen={paymentFeesModal} onClose={togglePaymentFeesModal} />

      {stripeModal && (
        <StripeModal>
          <div className="spinner-text">{t('onlineBooking.generalSettings.businessStripeAccount.stripeModalText')}</div>
        </StripeModal>
      )}
    </div>
  );
};
